import { getSalePaymentBalances, paymentBaseCents } from "./saleBalances";

const methodLabel = (method?: string) =>
    method === "CARD" ? "Card" : method === "CASH" ? "Cash" : method ? method.charAt(0) + method.slice(1).toLowerCase() : "Other";

const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

// "incl. $1.20 card fee + $6.24 tip" under a Paid line, or null when the
// payments carried neither.
export function paidExtrasLabel(cardFeeCents: number, tipCents: number): string | null {
    const parts = [
        cardFeeCents > 0 ? `${formatCents(cardFeeCents)} card fee` : null,
        tipCents > 0 ? `${formatCents(tipCents)} tip` : null,
    ].filter(Boolean);
    return parts.length ? `incl. ${parts.join(" + ")}` : null;
}

// What a closed sale was charged and how, from the recorded payments - not
// recalculated - so it reads the same whatever the tax / dual-pricing
// settings are today. The sale total excludes card fees (they're per
// payment) and tips, so Paid = Total + card fees + tips.
export function getPaidSaleSummary(sale: any) {
    const txs = (sale?.paymentTransactions || sale?.payment_transactions || []).filter(
        (t: any) =>
            !(t.preAuth || t.pre_auth) &&
            !["VOIDED", "REFUNDED"].includes(String(t.status ?? "").toUpperCase())
    );
    const subtotalCents = sale?.subtotalCents ?? sale?.subtotal_cents ?? 0;
    const taxCents = sale?.taxCents ?? sale?.tax_cents ?? 0;
    const discountCents = sale?.discountCents ?? sale?.discount_cents ?? 0;
    const hasDeposit = txs.some((t: any) => t.isDeposit || t.is_deposit);

    const payments = txs.map((t: any) => ({
        label: t.isDeposit || t.is_deposit ? "Deposit" : hasDeposit ? "Balance" : "Payment",
        amountCents: t.totalCents ?? t.total_cents ?? 0,
        method: methodLabel(t.paymentMethod ?? t.payment_method),
    }));
    const paidCents = payments.reduce((sum: number, p: any) => sum + p.amountCents, 0);
    const paidBase = txs.reduce((sum: number, t: any) => sum + paymentBaseCents(t), 0);
    const tipCents = txs.reduce((sum: number, t: any) => sum + (t.tipsCents || t.tips_cents || 0), 0);

    return {
        subtotalCents,
        taxCents,
        discountCents,
        totalCents: subtotalCents + taxCents - discountCents,
        paidCents,
        cardFeeCents: Math.max(0, paidCents - tipCents - paidBase),
        tipCents,
        payments,
    };
}

// Tax on a checkout, following the account's "Apply Tax to Services/Products"
// switches - the same rules PaymentPanel.calculateTax applies. Integer cents.
export function calculateCheckoutTaxCents({
    servicesCents,
    productsCents,
    discountCents = 0,
    taxPercentage,
    applyToServices,
    applyToProducts,
}: {
    servicesCents: number;
    productsCents: number;
    discountCents?: number;
    taxPercentage?: number | string | null;
    applyToServices?: boolean | null;
    applyToProducts?: boolean | null;
}): number {
    const taxable =
        (applyToServices ? servicesCents : 0) +
        (applyToProducts ? productsCents : 0) -
        (applyToServices || applyToProducts ? discountCents : 0);
    return Math.round((Math.max(0, taxable) * (Number(taxPercentage) || 0)) / 100);
}

// One breakdown for the calendar drawer and the payment panel, so both show
// lines that add up:
//   Subtotal + Tax - Discount = Total
//   Total - (Paid without its card fee) = Pending   (the cash price)
//   Pending + card fee = what's due by card
export function getCheckoutBreakdown({
    subtotalCents,
    taxCents,
    discountCents = 0,
    sale,
    account,
}: {
    subtotalCents: number;
    taxCents: number;
    discountCents?: number;
    sale: any;
    account: any;
}) {
    const totalCents = subtotalCents + taxCents - discountCents;
    const { paidCents, paidBaseCents, paidTipsCents, hasPriorPayment } = getSalePaymentBalances(sale);
    const pendingCents = Math.max(0, totalCents - paidBaseCents);
    const cardFeePercentage = account?.dualPricingEnabled ? Number(account?.cardFeePercentage) || 0 : 0;

    return {
        totalCents,
        paidCents,
        // The parts of earlier payments that were card fee or tip, not service.
        paidCardFeeCents: Math.max(0, paidCents - paidTipsCents - paidBaseCents),
        paidTipsCents,
        pendingCents,
        cardFeePercentage,
        pendingByCardCents: pendingCents + Math.round((pendingCents * cardFeePercentage) / 100),
        hasPriorPayment,
    };
}
